import React, { useState } from "react";
import { Layout } from "@/components/Layout";
import { PageHeader } from "@/components/PageHeader";
import { useListings } from "@/hooks/useListings";
import { TRIP_FILTERS, HERO_IMAGE } from "@/data/catalog";
import { useSave } from "@/hooks/useSave";
import { Bookmark } from "lucide-react";
import { toast } from "sonner";

const Travel = () => {
  const [filter, setFilter] = useState("All");
  const { save } = useSave();
  const { data: trips = [] } = useListings("trip");
  const { data: destinations = [] } = useListings("destination");
  const shown = filter === "All" ? trips : trips.filter((t) => t.trip_type === filter);

  return (
    <Layout>
      <PageHeader eyebrow="Travel" title="Luxury trips, curated for you." subtitle="Flights, hotels, experiences and group trips — effortlessly arranged." image={HERO_IMAGE} />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="travel-catalog">
        <div className="flex flex-wrap gap-3 mb-10">
          {TRIP_FILTERS.map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`trip-filter-${f.replace(/\s/g, "-").toLowerCase()}`} className={`px-5 py-2.5 rounded-full text-sm transition-colors duration-200 border ${filter === f ? "bg-gradient-to-r from-[#F3E5AB] to-[#D4AF37] text-[#1a1a12] border-transparent font-medium" : "border-amber-400/25 text-slate-300 hover:border-amber-400/50"}`}>{f}</button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {shown.map((t) => (
            <div key={t.id} data-testid={`trip-card-${t.id}`} className="group rounded-2xl overflow-hidden border border-white/10 hover:border-amber-400/40 transition-colors duration-500 bg-[#101017]">
              <div className="relative h-56 overflow-hidden">
                <img src={t.image} alt={t.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                {t.trip_type && <span className="absolute top-4 left-4 px-3 py-1 rounded-full glass text-xs text-champagne gold-border">{t.trip_type}</span>}
                <button onClick={() => save({ item_type: "trip", title: t.title, subtitle: t.trip_type, image: t.image, meta: { price: t.price } })} data-testid={`save-trip-${t.id}`} className="absolute top-4 right-4 w-9 h-9 rounded-full glass gold-border flex items-center justify-center text-champagne hover:bg-amber-400/20 transition-colors duration-200"><Bookmark size={15} /></button>
              </div>
              <div className="p-6">
                <h3 className="font-serif text-xl text-white">{t.title}</h3>
                <p className="text-sm text-slate-400 mt-2">{t.blurb}</p>
                <div className="flex items-center justify-between mt-5">
                  <span className="text-champagne font-medium">{t.price}</span>
                  <button onClick={() => toast.success("Booking request sent", { description: t.title })} data-testid={`book-trip-${t.id}`} className="text-sm px-4 py-2 rounded-full border border-amber-400/30 text-slate-200 hover:bg-amber-400/10 transition-colors duration-200">Request</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24" data-testid="travel-destinations">
        <h2 className="font-serif text-3xl sm:text-4xl text-white mb-10">Featured Destinations</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {destinations.map((d) => (
            <div key={d.id} className="group relative rounded-3xl overflow-hidden border border-amber-500/15 min-h-[340px] hover:border-amber-400/40 transition-colors duration-500">
              <img src={d.image} alt={d.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#070709] via-[#070709]/30 to-transparent" />
              <button onClick={() => save({ item_type: "destination", title: d.title, subtitle: d.subtitle, image: d.image, meta: { price: d.price } })} className="absolute top-4 right-4 w-9 h-9 rounded-full glass gold-border flex items-center justify-center text-champagne hover:bg-amber-400/20 transition-colors duration-200"><Bookmark size={15} /></button>
              <div className="relative h-full flex flex-col justify-end p-6"><span className="text-xs text-amber-300/90 mb-1">{d.price}</span><h3 className="font-serif text-xl text-white">{d.title}</h3><p className="text-slate-300 text-sm">{d.subtitle}</p></div>
            </div>
          ))}
        </div>
      </section>
    </Layout>
  );
};

export default Travel;
